import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-guard';
import { signOut } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  Download,
  LogOut,
  Menu,
  PenSquare,
  Search,
  Settings as SettingsIcon,
  Smartphone,
  X,
} from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PaletteDots } from '@/components/PalettePicker';
import { toast } from 'sonner';
import { ComposeDialog } from '@/components/ComposeDialog';
import { useTabSafeOpen } from '@/lib/use-tab-safe-open';
import { COMPOSE_EVENT } from '@/lib/compose-event';
import { useInstallPrompt } from '@/lib/use-install-prompt';
import { hasUnreadChangelog } from '@/lib/changelog';
import { hasUnvisitedMyDayToday } from '@/lib/daily';
import { cn } from '@/lib/utils';
import { NotificationsBell } from '@/components/NotificationsBell';
import { UserAvatar } from '@/components/UserAvatar';
import { InboxSwitcher } from '@/components/InboxSwitcher';
import { useCompanyBranding } from '@/lib/queries';
import { useAccess } from '@/lib/use-access';

// Navigation entries — a discriminated union so each entry is either a
// flat link or a dropdown group. The desktop bar renders entries in
// order; the mobile drawer flattens them with labelled section breaks.
//
// Grouping rationale:
//   Flat (4)        — daily-work destinations the team hits every shift.
//   Library  (2)    — reference surfaces consulted *while replying*.
//   Insights (2)    — analytical / look-back views (periodic).
//   Admin    (5)    — workspace config, admin-only.
//
// Server-side RLS still enforces the actual permission boundary on
// `adminOnly` groups; hiding here is just UX so members don't see
// links that 403 on click.
type NavLeaf = { to: string; label: string; /** workspace permission that shows this entry */ perm?: string };

type NavEntry =
  | {
      kind: 'item';
      to: string;
      label: string;
      /** Red dot when the user hasn't opened My Day yet today. */
      newTodayDot?: boolean;
      /** Red dot when there's a newer changelog entry than the user
       *  has seen. Used for "What's new" to draw the eye even when
       *  it's sitting alongside the daily-flow items. */
      unreadUpdatesDot?: boolean;
    }
  | { kind: 'group'; label: string; items: NavLeaf[] };

const NAV_ENTRIES: NavEntry[] = [
  { kind: 'item', to: '/my-day',   label: 'My Day', newTodayDot: true },
  { kind: 'item', to: '/dashboard', label: 'Tickets' },
  { kind: 'item', to: '/activity', label: 'Activity' },
  { kind: 'item', to: '/contacts', label: 'Contacts' },
  // Promoted from the user-avatar dropdown so feature releases get
  // seen. Visible to every team member; the unread dot is the
  // attention-pulling mechanism on new releases.
  { kind: 'item', to: '/changelog', label: "What's new", unreadUpdatesDot: true },
  {
    kind: 'group',
    label: 'Library',
    items: [
      { to: '/kb',        label: 'Knowledge base' },
      { to: '/templates', label: 'Reply templates' },
    ],
  },
  {
    kind: 'group',
    label: 'Insights',
    items: [
      { to: '/reports',  label: 'Reports' },
      { to: '/journeys', label: 'Client journeys' },
    ],
  },
  {
    kind: 'group',
    label: 'Admin',
    items: [
      { to: '/team',      label: 'Team members',     perm: 'team.view' },
      { to: '/roles',     label: 'Roles',            perm: 'team.view' },
      { to: '/access',    label: 'Who can do what',  perm: 'team.view' },
      { to: '/teams',     label: 'Groups',           perm: 'team.view' },
      { to: '/sla-rules', label: 'SLA rules',        perm: 'sla.manage' },
      { to: '/leave',     label: 'Leave requests',   perm: 'leave.approve' },
      { to: '/inboxes',   label: 'Inboxes',          perm: 'inboxes.manage' },
      { to: '/automations', label: 'Automations',    perm: 'automations.manage' },
      { to: '/company',   label: 'Company branding', perm: 'company.manage' },
    ],
  },
];

// How the bar gives way as the window narrows. The full bar (logo, inbox switcher, up
// to nine entries, search, Compose, bell, name) needs ~1370px, so each part has a
// width at which it steps back, in this order, and no label is ever allowed to wrap:
//   < 1440px  the email next to the avatar goes (the avatar menu still shows it)
//   < 1280px  Search and Compose become icon-only
//   < 1100px  the entries move into the menu button (the same drawer phones use)
// Before this, labels such as "My Day" and "What's new" wrapped onto two lines
// below ~1370px and below ~1200px the right-hand side was pushed off the screen.
export function TopNav() {
  const { teamMember, session } = useAuth();
  const email = session?.user?.email ?? '';
  const initial = (teamMember?.name ?? email).slice(0, 1).toUpperCase();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Tab-switch-safe + sessionStorage-persisted. Same hook used for every
  // dialog parent in the app — see lib/use-tab-safe-open.ts for the
  // mechanics. Survives Chrome tab returns and component remounts.
  const [composeOpen, setComposeOpen] = useTabSafeOpen('pa-compose-open');
  // "Email" buttons elsewhere (the contact page) ask for a compose window to a
  // given address via openComposeTo(); see lib/compose-event.ts.
  const [composePrefill, setComposePrefill] = useState<string | undefined>(undefined);
  useEffect(() => {
    const onOpen = (e: Event) => {
      setComposePrefill((e as CustomEvent<{ to?: string }>).detail?.to);
      setComposeOpen(true);
    };
    window.addEventListener(COMPOSE_EVENT, onOpen);
    return () => window.removeEventListener(COMPOSE_EVENT, onOpen);
  }, [setComposeOpen]);

  const location = useLocation();
  const navigate = useNavigate();

  // Admin gate — drives whether the "Admin" dropdown renders. Server-
  // side RLS still enforces the actual permission boundary; the nav
  // hide is just UX so non-admins don't see links that 403 on click.
  const access = useAccess();

  // Each entry shows by its OWN permission (migration 078); a group with no
  // visible entries disappears. The server refuses regardless: this is UX.
  const visibleEntries = NAV_ENTRIES.map((e) =>
    e.kind === 'group'
      ? { ...e, items: e.items.filter((i) => !i.perm || access.can(i.perm)) }
      : e,
  ).filter((e) => e.kind !== 'group' || e.items.length > 0);

  /** True if the current URL is anywhere under any of the given leaf
   *  routes. Used to keep a dropdown trigger looking active while
   *  you're inside one of its pages, mirroring NavLink's auto-active. */
  function groupIsActive(items: NavLeaf[]): boolean {
    return items.some(
      (i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'),
    );
  }

  // PWA install prompt — surfaces a "Install app" entry in the user
  // dropdown when the browser is offering install. Hidden once installed
  // or on browsers that don't fire `beforeinstallprompt` (e.g. iOS Safari,
  // where we instead show the iOS instructions row).
  const install = useInstallPrompt();
  async function handleInstallClick() {
    const outcome = await install.prompt();
    if (outcome === 'accepted') toast.success('Installed — find the app in your dock or home screen.');
  }

  // Read once per render — cheap, just a localStorage lookup. The route
  // itself flips the value to seen on mount, so navigating to /changelog
  // clears the dot for subsequent renders within the session.
  const hasUnreadUpdates = hasUnreadChangelog();
  // Red dot on "My Day" until the user opens it today. The route flips
  // this on mount (markMyDayVisitedToday), same as the changelog dot —
  // re-checked once per render (cheap localStorage read). Re-appears at
  // local midnight so it nudges a daily check-in.
  const showMyDayDot = hasUnvisitedMyDayToday();

  // Org-wide logo (migration 044). Renders in place of the text wordmark
  // once an admin uploads one on /company. Falls back to "PA Support" so
  // the nav never looks broken pre-upload.
  const brandingQ = useCompanyBranding();
  const logoUrl = brandingQ.data?.logo_url ?? null;

  // Auto-close the mobile menu on route change.
  const closeMobile = () => setMobileOpen(false);

  // Build a single-entry map so <UserAvatar> can render the signed-in user's
  // own avatar (or fall back to a chip with their initial). When `teamMember`
  // is null we render a plain chip with `initial` as before.
  const selfMap = teamMember
    ? new Map([
        [
          teamMember.slack_user_id,
          { name: teamMember.name, avatar_url: teamMember.avatar_url },
        ],
      ])
    : undefined;

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-11 max-w-screen-2xl items-center gap-3 px-4 sm:gap-5">
        <NavLink
          to="/my-day"
          className="flex shrink-0 items-center whitespace-nowrap text-sm font-semibold tracking-tight text-foreground"
          aria-label="Home"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              className="h-6 w-auto max-w-[140px] object-contain dark:rounded dark:bg-white dark:p-0.5"
            />
          ) : (
            <span>PA Support</span>
          )}
        </NavLink>
        {/* Inbox switcher — shows the current /inbox/:slug context + a
            dropdown to switch. Hidden on the smallest screens; the
            mobile drawer surfaces it separately. */}
        <div className="hidden md:block">
          <InboxSwitcher />
        </div>
        {/* Desktop nav (md+) — entries render either as a flat NavLink
            or as a dropdown trigger depending on `kind`. Active state
            on dropdowns mirrors NavLink's auto-active (location prefix
            match against any of the group's items). */}
        <nav className="hidden shrink-0 items-center gap-0.5 min-[1100px]:flex">
          {visibleEntries.map((entry) => {
            if (entry.kind === 'item') {
              const dot =
                (entry.newTodayDot && showMyDayDot) ||
                (entry.unreadUpdatesDot && hasUnreadUpdates);
              return (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  className={({ isActive }) =>
                    cn(
                      'relative whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium transition-colors duration-100',
                      isActive
                        ? 'text-foreground bg-accent'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/60',
                    )
                  }
                >
                  {entry.label}
                  {dot ? (
                    <span
                      aria-label="New for you today"
                      className="absolute right-0.5 top-0.5 inline-block h-1.5 w-1.5 rounded-full bg-tint-critical-fg ring-2 ring-background"
                    />
                  ) : null}
                </NavLink>
              );
            }
            // entry.kind === 'group'
            const groupActive = groupIsActive(entry.items);
            return (
              <DropdownMenu key={entry.label}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium transition-colors duration-100',
                      groupActive
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                  >
                    {entry.label}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {entry.items.map((item) => (
                    <DropdownMenuItem
                      key={item.to}
                      onClick={() => navigate(item.to)}
                      className={cn(
                        location.pathname === item.to && 'bg-accent text-foreground',
                      )}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/* Global search pill — single trigger for the ⌘K command palette,
              made visible so it's discoverable without knowing the shortcut.
              Clicking dispatches `pa:open-palette` on window; AuthedLayout
              listens and flips its `paletteOpen` state. We avoid prop-
              drilling the open-setter down through TopNav so this stays a
              one-call site that doesn't require changing the file's signature.
              On md+ screens shows the placeholder + ⌘K hint chip; on smaller
              screens collapses to an icon-only button so the row stays in
              one line beside Compose + Bell + avatar. */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('pa:open-palette'))}
            className="hidden h-7 items-center gap-2 rounded-md border border-border/70 bg-background/60 px-2 text-xs text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground sm:flex"
            title="Search tickets, contacts, KB, pages (Cmd/Ctrl+K)"
            aria-label="Open global search"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">Search…</span>
            <kbd className="ml-1 hidden h-4 items-center rounded border border-border/60 bg-muted px-1 font-mono text-3xs text-muted-foreground xl:inline-flex">
              ⌘K
            </kbd>
          </button>
          {/* Icon-only fallback for narrow screens where the pill doesn't
              fit. Same trigger, different shape. */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => window.dispatchEvent(new CustomEvent('pa:open-palette'))}
            className="h-7 w-7 p-0 sm:hidden"
            title="Search"
            aria-label="Open global search"
          >
            <Search className="h-3.5 w-3.5" />
          </Button>
          {/* Compose new ticket — primary action, kept distinct from "reply"
              UX so the team doesn't accidentally start a fresh thread when
              they meant to respond to an existing one. */}
          {access.canAny('tickets.create') ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setComposeOpen(true)}
            className="h-7 gap-1 px-2 text-xs"
            title="Compose new ticket — send the first email to a customer"
            aria-label="Compose"
          >
            <PenSquare className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">Compose</span>
          </Button>
          ) : null}
          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="relative h-7 gap-2 px-1.5 text-muted-foreground hover:text-foreground"
                aria-label="Account menu"
                title={email}
              >
                {teamMember?.slack_user_id ? (
                  <UserAvatar
                    slackId={teamMember.slack_user_id}
                    teamMembersBySlackId={selfMap}
                    size="sm"
                  />
                ) : (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-3xs font-medium text-background">
                    {initial}
                  </span>
                )}
                <span className="hidden max-w-[160px] truncate text-xs min-[1440px]:inline">
                  {email}
                </span>
                {/* The "new updates" dot used to live here too. It's
                    now carried by the "What's new" nav item next to
                    the daily-flow links, where it's far more
                    visible — see NAV_ENTRIES above. */}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {teamMember?.name ?? 'Team member'}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">{email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* Appearance. Shares one component with the sign-in page — see
                  components/ThemeToggle.tsx. */}
              <div className="px-2 py-1.5">
                <ThemeToggle />
                {/* Colour theme, as swatches rather than the full picker: the
                    grid in Settings → Appearance needs room for names, blurbs
                    and contrast figures, none of which fit a 224px dropdown.
                    These are here for discoverability and for the one-click
                    switch; the grid is where you actually browse. */}
                <PaletteDots className="mt-2 justify-between" />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <SettingsIcon className="mr-2 h-4 w-4" />
                <span>Settings &amp; signature</span>
              </DropdownMenuItem>
              {/* "What's new" used to live here. Moved to a top-nav
                  link so release notes get seen. */}
              {install.canInstall ? (
                <DropdownMenuItem onClick={handleInstallClick}>
                  <Download className="mr-2 h-4 w-4" />
                  <span>Install app</span>
                </DropdownMenuItem>
              ) : null}
              {!install.canInstall && install.isIos && !install.alreadyInstalled ? (
                <DropdownMenuItem
                  onClick={() =>
                    toast.info('On iOS Safari, tap the Share button → "Add to Home Screen"', {
                      duration: 6000,
                    })
                  }
                >
                  <Smartphone className="mr-2 h-4 w-4" />
                  <span>Install on iPhone…</span>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => {
                  void signOut();
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Menu button (< 1100px, where the entries no longer fit in the bar) */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            className="h-8 w-8 p-0 min-[1100px]:hidden"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Nav drawer (< 1100px). Overlays the page; nav links auto-
          close on tap because each NavLink onClick → closeMobile.
          Groups flatten with a labelled section break — a dropdown-
          inside-a-drawer is two interactions too many on mobile. */}
      {mobileOpen ? (
        <nav className="border-t border-border/60 bg-background min-[1100px]:hidden">
          <div className="mx-auto flex max-w-screen-2xl flex-col gap-0.5 px-3 py-2">
            {/* Inbox switcher — hidden from the top bar on mobile, surfaced
                here as a flat one-tap list (no nested dropdown) so phone users
                can switch inboxes + see new-ticket counts. Closes the drawer
                on switch. */}
            <div className="pb-2 md:hidden">
              <div className="px-1 pb-1 text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                Inbox
              </div>
              <InboxSwitcher variant="list" onNavigate={closeMobile} />
            </div>
            {visibleEntries.map((entry) => {
              if (entry.kind === 'item') {
                const isActive = location.pathname.startsWith(entry.to);
                const dot =
                (entry.newTodayDot && showMyDayDot) ||
                (entry.unreadUpdatesDot && hasUnreadUpdates);
                return (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    onClick={closeMobile}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-100',
                      isActive
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                  >
                    {entry.label}
                    {dot ? (
                      <span
                        aria-label="New for you today"
                        className="inline-block h-1.5 w-1.5 rounded-full bg-tint-critical-fg"
                      />
                    ) : null}
                  </NavLink>
                );
              }
              // entry.kind === 'group' — section header + flat items.
              return (
                <div key={entry.label} className="mt-2 border-t border-border/40 pt-2">
                  <div className="px-3 pb-1 text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                    {entry.label}
                  </div>
                  {entry.items.map((item) => {
                    const isActive = location.pathname.startsWith(item.to);
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={closeMobile}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-100',
                          isActive
                            ? 'bg-accent text-foreground'
                            : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                        )}
                      >
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </nav>
      ) : null}
      <ComposeDialog
        open={composeOpen && access.canAny('tickets.create')}
        onOpenChange={(o) => {
          setComposeOpen(o);
          if (!o) setComposePrefill(undefined);
        }}
        prefillTo={composePrefill}
      />
    </header>
  );
}
